import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { countries, CountryItem, countryFlagEmoji } from '../../data/countries';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { animations } from '../../design-system/tokens/animations';
import { shadows } from '../../design-system/tokens/shadows';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  width: 100%;
`;

const FieldLabel = styled.label<{ $error?: boolean }>`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => (props.$error ? props.theme.colors.error : props.theme.colors.text)};
  font-family: ${typography.fontFamily.primary};
`;

const Select = styled.select<{ $error?: boolean }>`
  padding: ${spacing[3]} ${spacing[4]};
  border: ${borders.width.base} solid
    ${props => (props.$error ? props.theme.colors.error : props.theme.colors.border)};
  border-radius: ${borders.radius.lg};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  transition: ${animations.transition(
    ['border-color', 'box-shadow'],
    animations.duration.fast,
    animations.easing.easeOut
  )};
  cursor: pointer;
  width: 100%;
  &:focus {
    outline: none;
    border-color: ${props => (props.$error ? props.theme.colors.error : props.theme.colors.primary)};
    box-shadow: ${props => shadows.colored.sm(props.$error ? props.theme.colors.error : props.theme.colors.primary)};
  }
`;

const ErrorText = styled.span`
  color: ${props => props.theme.colors.error};
  font-size: ${typography.fontSize.xs};
  font-family: ${typography.fontFamily.primary};
`;

export interface CountrySelectorProps {
  label: string;
  valueEn: string;
  valueGr: string;
  onChange: (nameEn: string, nameGr: string) => void;
  /** Placeholder for empty option (e.g. "Select country") */
  placeholder?: string;
  error?: string;
  required?: boolean;
}

function findCodeByNames(valueEn: string, valueGr: string): string {
  const en = (valueEn || '').trim();
  const gr = (valueGr || '').trim();
  if (!en && !gr) return '';
  const c = countries.find(
    x =>
      x.nameEn === en ||
      x.nameGr === gr ||
      x.nameEn.toLowerCase() === en.toLowerCase() ||
      x.nameGr === gr
  );
  return c ? c.code : '';
}

export const CountrySelector: React.FC<CountrySelectorProps> = ({
  label,
  valueEn,
  valueGr,
  onChange,
  placeholder,
  error,
  required = false,
}) => {
  const { t } = useTranslation();
  const reactId = useId();
  const selectId = `country-selector-${reactId}`;
  const selectedCode = findCodeByNames(valueEn, valueGr);
  const errorId = error ? `${selectId}-error` : undefined;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    if (!code) {
      onChange('', '');
      return;
    }
    const country = countries.find(c => c.code === code) as CountryItem | undefined;
    if (country) onChange(country.nameEn, country.nameGr);
  };

  return (
    <Wrapper>
      <FieldLabel as="label" htmlFor={selectId} $error={!!error}>
        {label}
        {required && <span style={{ color: 'var(--color-error, #dc3545)', marginLeft: 2 }}>*</span>}
      </FieldLabel>
      <Select
        id={selectId}
        value={selectedCode}
        onChange={handleChange}
        aria-invalid={!!error}
        aria-required={required || undefined}
        aria-describedby={errorId}
        $error={!!error}
      >
        <option value="">{placeholder ?? t('register.selectCountry')}</option>
        {countries.map(c => (
          <option key={c.code} value={c.code}>
            {countryFlagEmoji(c.code)} {c.nameEn} / {c.nameGr}
          </option>
        ))}
      </Select>
      {error && (
        <ErrorText id={errorId} role="alert">
          {error}
        </ErrorText>
      )}
    </Wrapper>
  );
};
